import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, CreditCard, ShieldCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { usePlanStatus, LIMIT_LABELS, fmtLimit, PLAN_EVENT } from "@/lib/usePlanStatus";
import { formatDeShort } from "@/lib/date";

const eur = (v) => (v === null || v === undefined ? "auf Anfrage" : `${v.toLocaleString("de-DE")} €`);

function Usage({ st }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      {Object.keys(LIMIT_LABELS).map((k) => {
        const lim = st.plan.limits[k];
        const pct = lim ? Math.min(100, (st.usage[k] / lim) * 100) : 0;
        return (
          <div key={k} className="border border-slate-200 bg-white p-4" data-testid={`tarif-usage-${k}`}>
            <div className="text-[10px] uppercase tracking-overline text-slate-500">{LIMIT_LABELS[k]}</div>
            <div className="text-2xl font-light mt-1">{st.usage[k]} <span className="text-sm text-slate-400">/ {fmtLimit(lim)}</span></div>
            <div className="h-1.5 bg-slate-100 mt-2"><div className={`h-full ${pct >= 100 ? "bg-red-600" : pct >= 80 ? "bg-amber-500" : "brand-active"}`} style={{ width: `${lim ? pct : 4}%` }} /></div>
          </div>
        );
      })}
    </div>
  );
}

function PlanCard({ p, current, onBuy, onChange, hasSub, busy }) {
  return (
    <div className={`border bg-white p-5 flex flex-col ${current ? "border-slate-900" : "border-slate-200"}`} data-testid={`tarif-plan-${p.key}`}>
      <div className="flex items-center justify-between"><div className="font-heading text-xl">{p.name}</div>{current && <span className="text-[10px] uppercase tracking-overline bg-slate-900 text-white px-2 py-0.5">Aktuell</span>}</div>
      <div className="text-2xl font-light mt-2">{eur(p.price)}{p.price != null && <span className="text-xs text-slate-500"> / Monat netto</span>}</div>
      <ul className="mt-4 space-y-1.5 text-sm text-slate-700 flex-1">
        {Object.keys(LIMIT_LABELS).map((k) => <li key={k} className="flex gap-2"><Check size={14} className="text-emerald-600 mt-0.5" />{fmtLimit(p.limits[k])} {LIMIT_LABELS[k]}</li>)}
        <li className={`flex gap-2 ${p.features?.audit ? "" : "text-slate-400 line-through"}`}><ShieldCheck size={14} className="mt-0.5" />Internes Audit</li>
      </ul>
      {p.online && p.price ? (
        hasSub && current ? <div className="mt-5 w-full py-2.5 text-sm text-center border border-slate-200 text-slate-500">Dein aktuelles Abo</div> : (
        <button disabled={busy} onClick={() => (hasSub ? onChange(p) : onBuy(p.key))} className="mt-5 w-full py-2.5 text-sm bg-slate-900 text-white hover:bg-black disabled:opacity-50 flex items-center justify-center gap-1.5" data-testid={`tarif-buy-${p.key}`}>
          <CreditCard size={14} /> {hasSub ? "Zu diesem Tarif wechseln" : "Monats-Abo starten"}
        </button>)
      ) : <a href="mailto:info@we-academy.de?subject=Enterprise-Tarif" className="mt-5 w-full py-2.5 text-sm border border-slate-300 text-center hover:bg-slate-50" data-testid={`tarif-contact-${p.key}`}>Anfragen</a>}
    </div>
  );
}

function AccessLog() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/tenant/access-log").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return (
    <div className="border border-slate-200 bg-white" data-testid="tarif-access-log">
      <div className="px-4 py-3 border-b border-slate-100 text-[10px] font-bold uppercase tracking-overline text-slate-500">Zugriffe durch den Plattform-Betreiber</div>
      {rows.length === 0 ? <div className="p-4 text-xs text-slate-400">Keine Zugriffe protokolliert.</div> : (
        <div className="max-h-64 overflow-y-auto text-xs font-mono">{rows.map((r, i) => <div key={i} className="px-4 py-1.5 border-t border-slate-50 flex gap-3"><span className="text-slate-400">{r.at.replace("T", " ").slice(0, 16)}</span><span>{r.by}</span><span className="text-slate-500">{r.method}</span><span className="truncate">{r.path}</span></div>)}</div>
      )}
    </div>
  );
}

export default function TarifTab() {
  const st = usePlanStatus(true);
  const [busy, setBusy] = useState(false);
  if (!st) return <div className="text-xs text-slate-400">Lädt…</div>;
  if (!st.tenant) return <div className="text-sm text-slate-500" data-testid="tarif-main">Der Haupt-Mandant hat keine Tarif-Grenzen. Tarife verwaltest du unter Plattform.</div>;
  const buy = async (plan) => {
    setBusy(true);
    try {
      const { data } = await api.post("/plan/checkout", { plan, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); setBusy(false); }
  };
  const change = async (p) => {
    if (!window.confirm(`Abo auf ${p.name} (${eur(p.price)} / Monat) umstellen? Die Differenz wird anteilig sofort verrechnet.`)) return;
    setBusy(true);
    try { await api.post("/plan/change", { plan: p.key }); toast.success(`Tarif ${p.name} aktiv`); window.dispatchEvent(new Event(PLAN_EVENT)); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const portal = async () => {
    try { const { data } = await api.post("/plan/portal", { origin_url: window.location.origin }); window.location.href = data.url; }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const subLabel = st.locked ? "Zugang gesperrt – Zahlung offen" : st.subscription_status === "past_due" || st.subscription_status === "unpaid" ? "Zahlung offen – bitte Zahlungsart aktualisieren (Sperre nach 7 Tagen)"
    : st.cancel_at_period_end ? "Abo gekündigt – endet am" : st.has_subscription ? "Monats-Abo aktiv · verlängert sich am" : st.expired ? "abgelaufen" : "bezahlt bis";
  return (
    <div className="space-y-6" data-testid="tarif-tab">
      <div className={`border p-4 text-sm flex items-center gap-3 flex-wrap ${st.expired || st.payment_failed_at ? "border-red-300 bg-red-50 text-red-800" : "border-slate-200 bg-white"}`} data-testid="tarif-current">
        <span>Aktueller Tarif: <b>{st.plan.name}</b> · {subLabel} {st.paid_until ? formatDeShort(st.paid_until.slice(0, 10)) : "—"}</span>
        {st.has_subscription && <button onClick={portal} className="ml-auto px-4 py-2 text-xs bg-slate-900 text-white hover:bg-black" data-testid="tarif-portal">Abo verwalten (Zahlungsart, Rechnungen, Kündigung)</button>}
      </div>
      <Usage st={st} />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">{st.plans.map((p) => <PlanCard key={p.key} p={p} current={p.key === st.plan.key} onBuy={buy} onChange={change} hasSub={st.has_subscription} busy={busy} />)}</div>
      <p className="text-[11px] text-slate-500">Monats-Abo per Stripe, verlängert sich automatisch. Tarifwechsel werden anteilig verrechnet; Kündigung jederzeit zum Monatsende über „Abo verwalten“. Ist eine Zahlung länger als 7 Tage offen, wird der Zugang gesperrt.</p>
      <AccessLog />
    </div>
  );
}
